import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Search, ChevronDown, ChevronUp, Building2, Globe, TrendingUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ORIENT_COLORS = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };

export const CompanyDatabase = ({ onViewDetail }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOrientation, setFilterOrientation] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [sortBy, setSortBy] = useState("cii_score");
  const [sortOrder, setSortOrder] = useState("desc");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/companies/leaderboard?limit=200`, { timeout: 30000 })
      .then(r => setCompanies(r.data.companies || []))
      .finally(() => setLoading(false));
  }, []);

  const countries = useMemo(() => [...new Set(companies.map(c => c.country).filter(Boolean))].sort(), [companies]);
  const orientations = useMemo(() => [...new Set(companies.map(c => c.value_orientation).filter(Boolean))].sort(), [companies]);

  const filtered = useMemo(() => {
    return companies.filter(c => {
      if (filterOrientation && c.value_orientation !== filterOrientation) return false;
      if (filterCountry && c.country !== filterCountry) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!(c.company_name || "").toLowerCase().includes(q) && !(c.domain || "").toLowerCase().includes(q)) return false;
      }
      return true;
    }).sort((a, b) => {
      const dir = sortOrder === "desc" ? -1 : 1;
      if (sortBy === "cii_score") return dir * ((a.cii_score || 0) - (b.cii_score || 0));
      if (sortBy === "product_count") return dir * ((a.product_count || 0) - (b.product_count || 0));
      if (sortBy === "company_name") return dir * (a.company_name || "").localeCompare(b.company_name || "");
      return 0;
    });
  }, [companies, filterOrientation, filterCountry, searchQuery, sortBy, sortOrder]);

  const SortHeader = ({ field, label }) => (
    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-400 cursor-pointer hover:text-white transition-colors"
      onClick={() => { setSortBy(field); setSortOrder(sortBy === field && sortOrder === "desc" ? "asc" : "desc"); }}>
      <span className="flex items-center gap-1">{label}
        {sortBy === field && (sortOrder === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
      </span>
    </th>
  );

  if (loading) return <div className="flex items-center justify-center py-32"><div className="w-10 h-10 rounded-full border-2 border-amber-500/30 border-t-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="company-database">
      <div>
        <h2 className="text-xl font-black text-white">Company Database</h2>
        <p className="text-xs text-slate-400">{filtered.length.toLocaleString()} of {companies.length.toLocaleString()} companies</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-500" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search companies or domains..."
            className="w-full pl-8 pr-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/30"
            data-testid="company-db-search" />
        </div>
        <select value={filterOrientation} onChange={e => setFilterOrientation(e.target.value)}
          className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="filter-orientation">
          <option value="">All Orientations</option>
          {orientations.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={filterCountry} onChange={e => setFilterCountry(e.target.value)}
          className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="filter-country">
          <option value="">All Countries</option>
          {countries.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
        <table className="w-full text-xs" data-testid="company-table">
          <thead className="bg-white/[0.03] border-b border-white/[0.06]">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase text-slate-500 w-8">#</th>
              <SortHeader field="company_name" label="Company" />
              <th className="px-3 py-2 text-left text-[10px] uppercase text-slate-400">Domain</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase text-slate-400">Country</th>
              <SortHeader field="cii_score" label="CII" />
              <th className="px-3 py-2 text-left text-[10px] uppercase text-slate-400">Orientation</th>
              <SortHeader field="product_count" label="Products" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-slate-500">No companies match your filters</td></tr>
            ) : filtered.slice(0, 100).map((c, i) => (
              <tr key={c.domain || `co-${i}`}
                className="border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer transition-colors"
                onClick={() => setExpanded(expanded === c.domain ? null : c.domain)}
                data-testid={`company-row-${i}`}>
                <td className="px-3 py-2.5 text-slate-500 font-mono">{i + 1}</td>
                <td className="px-3 py-2.5 font-medium text-white flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                  {onViewDetail ? (
                    <button onClick={(e) => { e.stopPropagation(); onViewDetail(c.domain); }}
                      className="hover:text-amber-300 transition-colors text-left"
                      data-testid={`view-detail-${c.domain}`}>
                      {c.company_name}
                    </button>
                  ) : c.company_name}
                </td>
                <td className="px-3 py-2.5 text-slate-400 font-mono">{c.domain}</td>
                <td className="px-3 py-2.5 text-slate-300 flex items-center gap-1">
                  <Globe className="w-3 h-3 text-slate-500" />{c.country}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`font-bold ${(c.cii_score || 0) >= 60 ? 'text-emerald-400' : (c.cii_score || 0) >= 30 ? 'text-amber-400' : 'text-red-400'}`}>
                    {c.cii_score?.toFixed(1) || "—"}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                    style={{ color: ORIENT_COLORS[c.value_orientation] || "#94a3b8", background: `${ORIENT_COLORS[c.value_orientation] || "#94a3b8"}15` }}>
                    {c.value_orientation}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-slate-300 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-slate-500" />{c.product_count || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length > 100 && (
        <p className="text-center text-[10px] text-slate-500">Showing 100 of {filtered.length.toLocaleString()} — use filters to narrow down</p>
      )}
    </div>
  );
};
